import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './restaurantManager.css';
import '../../../../Company/list/CompanyCard/CompanyCard.css';
import { shopIcon, locationIcon, chefHatIcon } from '../../../../../utils/icons';
import ProfileView from '../../ProfileView';
import SignedImage from '../../../SignedImage/SignedImage';

const RestaurantManager = () => {
  const navigate = useNavigate();
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingRestaurantId, setEditingRestaurantId] = useState(null);

  useEffect(() => {
    fetchRestaurants();
  }, []);

  const fetchRestaurants = async () => {
    try {
      setLoading(true);
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_STAGING_URL}/api/user-info`, {
        method: 'GET',
        credentials: 'include',
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch restaurants: ${response.status} ${errorText}`);
      }

      const data = await response.json();
      setRestaurants(data.restaurants || []);
    } catch (err) {
      console.error('Error fetching restaurants:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNew = () => {
    // Show the profile form inline for creating a new restaurant
    setShowCreateForm(true);
    setEditingRestaurantId(null);
  };

  const handleEditRestaurant = (restaurantId) => {
    // Show the profile form inline for editing the restaurant
    setShowCreateForm(true);
    setEditingRestaurantId(restaurantId);
  };

  const handleFormClose = () => {
    // Hide the form and refresh the restaurant list
    setShowCreateForm(false);
    setEditingRestaurantId(null);
    
    // Add a small delay to ensure backend has processed the update
    setTimeout(() => {
      fetchRestaurants(); // Refresh the list after creating/editing
      
      // Notify other components that restaurant data has been updated
      window.dispatchEvent(new CustomEvent('restaurantUpdated'));
    }, 500);
  };

  const handleSwitchRestaurant = async (restaurantId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_STAGING_URL}/api/switch-restaurant`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ restaurantId }),
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to switch restaurant');
      }

      // Reload page to update context
      window.location.reload();
    } catch (err) {
      console.error('Error switching restaurant:', err);
      alert(`Error al cambiar de restaurante: ${err.message}`);
    }
  };

  const handleDeleteRestaurant = async (restaurantId, restaurantName) => {
    const confirmDelete = window.confirm(
      `¿Estás seguro de que quieres eliminar el restaurante "${restaurantName}"? Esta acción no se puede deshacer.`
    );

    if (!confirmDelete) {
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_STAGING_URL}/api/company/${restaurantId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete restaurant');
      }

      // Refresh the restaurant list
      await fetchRestaurants();
      
      // Notify other components that restaurant data has been updated
      window.dispatchEvent(new CustomEvent('restaurantUpdated'));
      
      alert('Restaurante eliminado exitosamente');
    } catch (err) {
      console.error('Error deleting restaurant:', err);
      alert(`Error al eliminar restaurante: ${err.message}`);
    }
  };

  const handleViewProfile = (restaurantId) => {
    try {
      const path = '/empresas/' + restaurantId;
      navigate(path);
    } catch (err) {
      console.error('Error navigating to profile:', err);
    }
  };

  if (loading) {
    return (
      <div className="restaurant-manager">
        <div className="restaurant-manager__header">
          <h1>Mis Restaurantes</h1>
        </div>
        <div className="restaurant-manager__loading">
          <div>Cargando restaurantes...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="restaurant-manager">
        <div className="restaurant-manager__header">
          <h1>Mis Restaurantes</h1>
        </div>
        <div className="restaurant-manager__error">
          <div>Error: {error}</div>
          <button onClick={fetchRestaurants}>Reintentar</button>
        </div>
      </div>
    );
  }


  // If showing the form, render ProfileView instead
  if (showCreateForm) {
    return (
      <div className="restaurant-manager">
        <div className="restaurant-manager__header">
          <h1>{editingRestaurantId ? 'Editar Restaurante' : 'Crear Nuevo Restaurante'}</h1>
          <button 
            className="restaurant-manager__create-button"
            onClick={handleFormClose}
          >
            ← Volver a Restaurantes
          </button>
        </div>
        <ProfileView 
          userType={'empresas'} 
          restaurantId={editingRestaurantId}
          onFormSubmit={handleFormClose}
          isNewRestaurant={!editingRestaurantId}
        />
      </div>
    );
  }

  return (
    <div className="restaurant-manager">
      <div className="restaurant-manager__header">
        <h1>Mis Restaurantes</h1>
        <button 
          className="restaurant-manager__create-button"
          onClick={handleCreateNew}
        >
          + Crear Nuevo Restaurante
        </button>
      </div>

      <div className="restaurant-manager__list">
        {restaurants.length === 0 ? (
          <div className="restaurant-manager__empty">
            <div className="restaurant-manager__empty-icon">🏪</div>
            <h3>No tienes restaurantes creados</h3>
            <p>Crea tu primer restaurante para comenzar a gestionar tu negocio</p>
            <button 
              className="restaurant-manager__create-button"
              onClick={handleCreateNew}
            >
              Crear Mi Primer Restaurante
            </button>
          </div>
        ) : (
          restaurants.map((restaurant) => (
            <div key={restaurant.restaurantId} className="company-card__container">
              <div className="company-card__image-container">
                <SignedImage 
                  src={restaurant.restaurantImage}
                  alt={restaurant.restaurantName}
                  className="company-conversation-card__image"
                  fallbackSrc="/default-restaurant.png"
                />
              </div>
              
              <div className="company-card__description">
                <h3>{restaurant.restaurantName}</h3>
                <div>
                  <div className="company-card__icon-title-container">
                    {shopIcon}
                    <div className="company-card__info-container"> 
                      <p><strong>Establecimiento:</strong></p> 
                      <p>{restaurant.format || 'No especificado'}</p>
                    </div>
                  </div>
                  <div className="company-card__icon-title-container"> 
                    {locationIcon}
                    <div className="company-card__info-container">
                      <p><strong>Ubicación:</strong></p> 
                      <p>{restaurant.region && restaurant.comuna ? `${restaurant.region}, ${restaurant.comuna}` : 'Ubicación no especificada'}</p>
                    </div>
                  </div>
                  <div className="company-card__icon-title-container">
                    {chefHatIcon}
                    <div className="company-card__info-container">
                      <p><strong>Especialidad:</strong></p> 
                      <p>{restaurant.specialty || 'No especificada'}</p>
                    </div>
                  </div>
                </div>
                {restaurant.restaurantDescription && (
                  <div className="company-card__description-footer">
                    <p>{restaurant.restaurantDescription}</p>
                  </div>
                )}
              </div>
              
              <div className="company-card__buttons">
                <button 
                  className="company-card__button"
                  onClick={() => handleViewProfile(restaurant.restaurantId)}
                >
                  Perfil
                </button>
                <button 
                  className="company-card__button"
                  onClick={() => handleEditRestaurant(restaurant.restaurantId)}
                >
                  Editar
                </button>
                <button 
                  className="company-card__button"
                  onClick={() => handleDeleteRestaurant(restaurant.restaurantId, restaurant.restaurantName)}
                >
                  Eliminar
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default RestaurantManager;
